import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getOrderUuid, getSelectedExhibitionId } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import {
  BookingWorkshop,
  PostWorkshopBooking,
  PostWorkshopResponse,
  RemoveBookingWorkshop,
  RemoveBookingWorkshopAssignedHolderTariffs,
  SetBookingWorkshop
} from '@products/models/booking.model';
import { getProductType } from '@products/models/products.model';
import { WorkshopAssignedHolderTariffs, WorkshopAvailableSeats } from '@products/models/workshop-status.model';
import {
  WorkshopId,
  WorkshopProduct,
  WorkshopProductList,
  WorkshopTariffHolder
} from '@products/models/workshop.model';
import { environment } from '@src/environments/environment';
import { intersection, keys } from 'lodash';
import {
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  first,
  forkJoin,
  from,
  map,
  Observable,
  pipe,
  switchMap,
  takeWhile,
  toArray
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WorkshopBookingService {
  private readonly store = inject(Store);
  private readonly http = inject(HttpClient);

  postReleaseWorkshopBookings$(orderUuid: string) {
    return this.http.post<void>(
      `${environment.protocol}${environment.webApiUrl}/order/workshop-booking/${orderUuid}/release`,
      {}
    );
  }

  setWorkshopBookings$(setBookingWorkshops: SetBookingWorkshop[]): Observable<PostWorkshopBooking[]> {
    return combineLatest([this.store.select(getSelectedExhibitionId), this.store.select(getOrderUuid)]).pipe(
      first((combinedLatest) => combinedLatest.every((combinedItem) => !!combinedItem)),
      map(([eventId, orderUuid]) =>
        this.mapSetBookingWorkshopsToPostWorkshopBookings(setBookingWorkshops, eventId, orderUuid)
      )
    );
  }

  removeWorkshopBookings$(removeBookingWorkshops: RemoveBookingWorkshop[]): Observable<PostWorkshopBooking[]> {
    return combineLatest([this.store.select(getSelectedExhibitionId), this.store.select(getOrderUuid)]).pipe(
      first((combinedLatest) => combinedLatest.every((combinedItem) => !!combinedItem)),
      map(([eventId, orderUuid]) =>
        this.mapRemoveBookingWorkshopsToPostWorkshopBookings(removeBookingWorkshops, eventId, orderUuid)
      )
    );
  }

  postAvailableWorkshopBookings$(
    postWorkshopBookings: PostWorkshopBooking[]
  ): Observable<{ numberOftWorkshopBookingsAssigned: number; workshopAvailableSeats: WorkshopAvailableSeats }> {
    let isWorkshopSoldOut = false;

    const postWorkshopBookings$ = from(
      postWorkshopBookings.map((postWorkshopBooking) => {
        return this.postWorkshopBooking$(postWorkshopBooking);
      })
    );

    return postWorkshopBookings$.pipe(
      concatMap((postWorkshopBooking$) =>
        postWorkshopBooking$.pipe(map((postWorkshopBookingResponse) => postWorkshopBookingResponse))
      ),
      catchError(() => {
        // API throws an error when workshop is sold out
        isWorkshopSoldOut = true;

        return EMPTY;
      }),
      takeWhile(() => !isWorkshopSoldOut),
      toArray(),
      map((postWorkshopBookingResponses) => {
        const { workshopId, seats } = postWorkshopBookingResponses.reduce((previousResponse, nextResponse) =>
          previousResponse.seats < nextResponse.seats ? previousResponse : nextResponse
        );

        return {
          numberOftWorkshopBookingsAssigned: postWorkshopBookingResponses.length,
          workshopAvailableSeats: { [workshopId]: seats }
        };
      })
    );
  }

  postRemoveMultipleWorkshopBookings$(
    removeBookingWorkshops: RemoveBookingWorkshop[],
    postWorkshopBookings: PostWorkshopBooking[]
  ): Observable<{ removeBookingWorkshops: RemoveBookingWorkshop[]; workshopAvailableSeats: WorkshopAvailableSeats }> {
    return forkJoin(
      postWorkshopBookings.map((postWorkshopBooking) => {
        return this.postWorkshopBooking$(postWorkshopBooking);
      })
    ).pipe(
      map((postWorkshopBookingResponses) => {
        const workshopAvailableSeats: WorkshopAvailableSeats = {};

        postWorkshopBookingResponses.forEach(({ workshopId, seats }) => (workshopAvailableSeats[workshopId] = seats));

        return {
          removeBookingWorkshops,
          workshopAvailableSeats
        };
      })
    );
  }

  postReleaseAssignedWorkshopHolderSeats$(
    workshopAssignedHolderTariffs: WorkshopAssignedHolderTariffs,
    releaseWorkshopAssignedHolderTariffs: WorkshopAssignedHolderTariffs
  ): Observable<{
    workshopAssignedHolderTariffs: WorkshopAssignedHolderTariffs;
    workshopAvailableSeats: WorkshopAvailableSeats;
  }> {
    return combineLatest([this.store.select(getSelectedExhibitionId), this.store.select(getOrderUuid)]).pipe(
      first((combinedLatest) => combinedLatest.every((combinedItem) => !!combinedItem)),
      map(([eventId, orderUuid]) => {
        const postWorkshopBookings: PostWorkshopBooking[] = [];

        keys(releaseWorkshopAssignedHolderTariffs).forEach((workshopId: WorkshopId | string) => {
          const workshopSeatsToRelease = releaseWorkshopAssignedHolderTariffs[workshopId].length;

          if (workshopSeatsToRelease) {
            const postWorkshopBooking: PostWorkshopBooking = {
              eventId,
              workshopId: +workshopId,
              seats: -Math.abs(workshopSeatsToRelease),
              uuid: orderUuid
            };

            postWorkshopBookings.push(postWorkshopBooking);
          }
        });

        return postWorkshopBookings;
      }),
      switchMap((postWorkshopBookings) =>
        forkJoin(
          postWorkshopBookings.map((postWorkshopBooking) => {
            return this.postWorkshopBooking$(postWorkshopBooking);
          })
        ).pipe(
          map((postWorkshopBookingResponses) => {
            const workshopAvailableSeats: WorkshopAvailableSeats = {};

            keys(releaseWorkshopAssignedHolderTariffs).forEach((workshopId: WorkshopId | string) => {
              const releasedWorkshopSeats = postWorkshopBookingResponses.find(
                (postWorkshopBookingResponse) => postWorkshopBookingResponse.workshopId === +workshopId
              );

              if (releasedWorkshopSeats) {
                workshopAvailableSeats[workshopId] = releasedWorkshopSeats.seats;
              } else {
                workshopAssignedHolderTariffs[workshopId] = [
                  ...workshopAssignedHolderTariffs[workshopId],
                  ...releaseWorkshopAssignedHolderTariffs[workshopId]
                ];
              }
            });

            return {
              workshopAssignedHolderTariffs,
              workshopAvailableSeats
            };
          })
        )
      )
    );
  }

  removeUnassignedTariffsInSetBookingWorkshopsPipe(
    setBookingWorkshops: SetBookingWorkshop[],
    postWorkshopBookings: PostWorkshopBooking[]
  ) {
    return pipe(
      map((postAvailableWorkshopBookingsResponse) => {
        const { workshopAvailableSeats, numberOftWorkshopBookingsAssigned } = postAvailableWorkshopBookingsResponse as {
          numberOftWorkshopBookingsAssigned: number;
          workshopAvailableSeats: WorkshopAvailableSeats;
        };

        const postWorkshopBookingsAssigned = postWorkshopBookings.slice(0, numberOftWorkshopBookingsAssigned);
        const postWorkshopBookingsAssignedTariffUuids = postWorkshopBookingsAssigned.map(
          (postWorkshopBookingAssigned) => postWorkshopBookingAssigned.tariffUuid
        );

        setBookingWorkshops.forEach((setBookingWorkshop) => {
          setBookingWorkshop.workshops.forEach((workshop) => {
            workshop.tariffUuids = intersection(workshop.tariffUuids, postWorkshopBookingsAssignedTariffUuids);
          });

          setBookingWorkshop.workshops = setBookingWorkshop.workshops.filter(
            (workshop) => !!workshop.tariffUuids.length
          );
        });

        const setBookingWorkshopsWithAssignedTariffUuids = setBookingWorkshops.filter(
          (setBookingWorkshop) => !!setBookingWorkshop.workshops.length
        );

        return {
          setBookingWorkshops: setBookingWorkshopsWithAssignedTariffUuids,
          workshopAvailableSeats
        };
      })
    );
  }

  mapSetBookingWorkshopsToPostWorkshopBookings(
    setBookingWorkshops: SetBookingWorkshop[],
    eventId: number,
    orderUuid: string
  ): PostWorkshopBooking[] {
    const postWorkshopBookings: PostWorkshopBooking[] = [];

    setBookingWorkshops.forEach((setBookingWorkshop) => {
      setBookingWorkshop.workshops.forEach((bookingWorkshop) => {
        bookingWorkshop.tariffUuids.forEach((tariffUuid) => {
          const postWorkshopBooking: PostWorkshopBooking = {
            eventId,
            uuid: orderUuid,
            workshopId: bookingWorkshop.id,
            seats: 1,
            tariffUuid
          };

          postWorkshopBookings.push(postWorkshopBooking);
        });
      });
    });

    return postWorkshopBookings;
  }

  mapRemoveBookingWorkshopsToPostWorkshopBookings(
    removeBookingWorkshops: RemoveBookingWorkshop[],
    eventId: number,
    orderUuid: string
  ): PostWorkshopBooking[] {
    const removeWorkshopSeatsByWorkshopId: { [workshopId: number]: number } = {};
    const postWorkshopBookings: PostWorkshopBooking[] = [];

    removeBookingWorkshops.forEach((removeBookingWorkshop) => {
      removeBookingWorkshop.removeWorkshopsAssignedHolderTariffs.forEach((removeWorkshopAssignedHolderTariffs) => {
        removeWorkshopAssignedHolderTariffs.removeTariffUuids.forEach(() => {
          if (!removeWorkshopSeatsByWorkshopId[removeWorkshopAssignedHolderTariffs.workshopId]) {
            removeWorkshopSeatsByWorkshopId[removeWorkshopAssignedHolderTariffs.workshopId] = 0;
          }

          removeWorkshopSeatsByWorkshopId[removeWorkshopAssignedHolderTariffs.workshopId]--;
        });
      });
    });

    for (const workshopId in removeWorkshopSeatsByWorkshopId) {
      const postWorkshopBooking: PostWorkshopBooking = {
        eventId,
        uuid: orderUuid,
        workshopId: +workshopId,
        seats: removeWorkshopSeatsByWorkshopId[workshopId]
      };

      postWorkshopBookings.push(postWorkshopBooking);
    }

    return postWorkshopBookings;
  }

  mapWorkshopTariffHoldersToSetBookingWorkshops(
    setWorkshopTariffHolders: WorkshopTariffHolder[],
    workshopProductList: WorkshopProductList
  ): SetBookingWorkshop[] {
    const setBookingWorkshops: SetBookingWorkshop[] = [];

    setWorkshopTariffHolders.forEach((workshopTariffHolder) => {
      const { ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex, workshopId, tariffUuid } =
        workshopTariffHolder;

      const productBooking = setBookingWorkshops.find(
        (setProductBooking) =>
          setProductBooking.ticketTypeId === ticketTypeId &&
          setProductBooking.ticketPersonId === ticketPersonId &&
          setProductBooking.voucherCode === voucherCode &&
          setProductBooking.packageNumber === packageNumber &&
          setProductBooking.packageIndex === packageIndex
      );

      if (productBooking) {
        const productBookingWorkshop = productBooking.workshops.find(
          (bookingWorkshop) => bookingWorkshop.id === workshopId
        );

        if (productBookingWorkshop) {
          productBookingWorkshop.tariffUuids.push(tariffUuid);
        }
      } else {
        const { workshopName, price }: WorkshopProduct = workshopProductList.find(
          (workshopProduct) => workshopProduct.workshopId === workshopId
        );

        const bookingWorkshop: BookingWorkshop = {
          id: workshopId,
          name: workshopName,
          price,
          tariffUuids: [tariffUuid]
        };

        const setBookingWorkshop: SetBookingWorkshop = {
          productType: getProductType(workshopTariffHolder),
          ticketTypeId,
          ticketPersonId,
          voucherCode,
          packageNumber,
          packageIndex,
          workshops: [bookingWorkshop]
        };

        setBookingWorkshops.push(setBookingWorkshop);
      }
    });

    return setBookingWorkshops;
  }

  mapWorkshopTariffHoldersToRemoveBookingWorkshops(
    removeWorkshopTariffHolders: WorkshopTariffHolder[]
  ): RemoveBookingWorkshop[] {
    const removeBookingWorkshops: RemoveBookingWorkshop[] = [];

    removeWorkshopTariffHolders.forEach((workshopTariffHolder) => {
      const { ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex, workshopId, tariffUuid } =
        workshopTariffHolder;

      const createdRemoveBookingWorkshop = removeBookingWorkshops.find(
        (removeBookingWorkshop) =>
          removeBookingWorkshop.ticketTypeId === ticketTypeId &&
          removeBookingWorkshop.ticketPersonId === ticketPersonId &&
          removeBookingWorkshop.voucherCode === voucherCode &&
          removeBookingWorkshop.packageNumber === packageNumber &&
          removeBookingWorkshop.packageIndex === packageIndex
      );

      if (createdRemoveBookingWorkshop) {
        const createdRemoveBookingWorkshopTariffUuids =
          createdRemoveBookingWorkshop.removeWorkshopsAssignedHolderTariffs.find(
            (removeBookingWorkshop) => removeBookingWorkshop.workshopId === workshopId
          );

        if (createdRemoveBookingWorkshopTariffUuids) {
          createdRemoveBookingWorkshopTariffUuids.removeTariffUuids.push(tariffUuid);
        } else {
          const removeBookingWorkshopAssignedHolderTariffs: RemoveBookingWorkshopAssignedHolderTariffs = {
            workshopId,
            removeTariffUuids: [tariffUuid]
          };

          createdRemoveBookingWorkshop.removeWorkshopsAssignedHolderTariffs.push(
            removeBookingWorkshopAssignedHolderTariffs
          );
        }
      } else {
        const removeBookedWorkshopAssignedHolderTariffs: RemoveBookingWorkshopAssignedHolderTariffs = {
          workshopId,
          removeTariffUuids: [tariffUuid]
        };

        const removeBookingWorkshop: RemoveBookingWorkshop = {
          productType: getProductType(workshopTariffHolder),
          ticketTypeId,
          ticketPersonId,
          voucherCode,
          packageNumber,
          packageIndex,
          removeWorkshopsAssignedHolderTariffs: [removeBookedWorkshopAssignedHolderTariffs]
        };

        removeBookingWorkshops.push(removeBookingWorkshop);
      }
    });

    return removeBookingWorkshops;
  }

  private postWorkshopBooking$(postWorkshopBooking: PostWorkshopBooking): Observable<PostWorkshopResponse> {
    return this.http.post<PostWorkshopResponse>(
      `${environment.protocol}${environment.webApiUrl}/order/workshop-booking`,
      postWorkshopBooking
    );
  }
}
